import type { Address } from "viem"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { isAddress } from "viem"
import { useAccount, useConfig } from "wagmi"

import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { transferLockInteraction } from "@/domains/interactions"
import { makeBreadcrumbs } from "@/domains/lock-page/breadcrumbs"
import { TransferCard } from "@/domains/lock-page/components/card/transfer-card/components/transfer-card"
import { LockPageLayout } from "@/domains/lock-page/components/lock-page-layout"
import { LockPageTabs } from "@/domains/lock-page/components/lock-page-tabs"
import { MANAGE_LOCK_TABS } from "@/domains/lock-page/constants"
import { ComplexTab } from "@/domains/navigation/components/complex-breadcrumb-dropdown"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useNavigateOnLockCleared } from "@/hooks/use-navigate-on-lock-cleared"
import { useSyncLockIdFromRouter } from "@/hooks/use-sync-lock-id-from-router"
import { useVestedToken } from "@/hooks/use-vested-token"
import useEventEmitter from "@/hooks/useEventEmitter"

export default observer(() => {
  const navigate = useNavigate()
  const account = useAccount()
  const config = useConfig()
  const chainConfig = useChainConfig()
  const emitter = useEventEmitter()
  const vestedToken = useVestedToken()
  const scaleApp = useScaleApplication()
  const breadcrumbs = makeBreadcrumbs()

  useSyncLockIdFromRouter()
  useNavigateOnLockCleared()

  const lock = scaleApp.Pages.Lock.selectedLock
  const nftId = lock?.id

  const [destAddress, setDestAddress] = useState("")
  const isValidAddress = isAddress(destAddress)
  const shouldDisplayError = !isValidAddress && destAddress !== ""

  const hasVotedThisEpoch = Boolean(lock?.hasVotedThisEpoch)
  const hasExpired = Boolean(lock?.hasExpired)
  const isReset = Boolean(lock?.isReset)
  const canTransfer = Boolean(lock?.canTransfer) && (!hasExpired || (hasExpired && isReset))

  const onTransferNftClick = async (nftId: number, address: string) => {
    const result = await transferLockInteraction({
      chainId: chainConfig.chainId,
      config,
      vestedAddress: vestedToken.address,
      account: account.address,
      toAccount: address as Address,
      tokenId: nftId,
      emitter,
      eventBus: scaleApp.EventBus,
    })

    if (result?.success) {
      navigate("/lock")
    }
  }

  const complexTab: ComplexTab | undefined = MANAGE_LOCK_TABS[4]

  if (!complexTab) {
    return null
  }

  const title = `Transfer`

  return (
    <PageContainer>
      <MetaData title={title} />
      <PageTitle title={title} items={breadcrumbs} />
      <TooltipProvider>
        <LockPageLayout>
          <LockPageTabs />
          {lock && nftId && (
            <TransferCard
              nftId={nftId}
              destAddress={destAddress}
              isValidAddress={isValidAddress}
              shouldDisplayError={shouldDisplayError}
              hasExpired={hasExpired}
              isReset={isReset}
              canTransfer={canTransfer}
              hasVotedThisEpoch={hasVotedThisEpoch}
              icon={complexTab.icon ? <complexTab.icon /> : undefined}
              onAddressChange={setDestAddress}
              onTransfer={async () => {
                await onTransferNftClick(lock.id, destAddress)
              }}
            />
          )}
        </LockPageLayout>
      </TooltipProvider>
    </PageContainer>
  )
})
